import {tokens, useTokenValue, view} from '@sail/ui';
import {useMemo, useRef} from 'react';
import {
  Conversation,
  ConversationLogItem,
  ConversationMessage,
  InlineDataConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';
import {useTheme} from '../ThemeProvider';
import {MessageAddedRow} from '../Conversation/Log/MessageAddedRow';
import {ParticipantJoinedRow} from '../Conversation/Log/ParticipantJoinedRow';
import {ParticipantLeftRow} from '../Conversation/Log/ParticipantLeftRow';
import {ChatEndedRow} from '../Chat/Row/ChatEndedRow';
import {TranslationRow} from '../Chat/Row/TranslationRow';
import {MetaEventRow} from '../Conversation/Log/MetaEventRow';
import {GroupedMessagesRowV2} from '../Conversation/Log/Messages/GroupedMessagesRowV2';
import {CustomComponent} from '../Conversation/Log/Messages/CustomComponent';
import {avatarForMessage} from '../Conversation/Log/Messages/avatarForMessage';
import {AvatarSpacer} from '../Chat/AvatarSpacer';
import {LoadingText} from '../Conversation/LoadingText';
import {LoadingTextAsync} from '../Conversation/LoadingTextAsync';
import {BlinkingCursor} from '../Conversation/BlinkingCursor';
import {TypingIndicatorNext} from '../Conversation/TypingIndicatorNext';
import {useMessagesTopAnchored} from './useMessagesTopAnchored';
import {ChatBubbleNextConfig} from '../Chat/Row/ChatBubbleNext';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../FeatureFlagsProvider';
import {FeedbackContainer} from '../Conversation/FeedbackContainer';
import {didUserSelectThumbsDownAdvanceButtonOptionsMessage} from './message-added';
import {Theme} from '../theme';

export type GroupingOptions = {
  useNewDesign?: boolean;
  useDotComDesign?: boolean;
  animated?: boolean;
  disableFeedback?: boolean;
  disableInteraction?: boolean;
  messageRenderer?: (msg: ConversationMessage) => JSX.Element | null;
  shouldAddLoadingIndicators?: boolean;
  loading?: boolean;
  loadingMessage?: string;
};

type GroupingState = {
  items: Array<JSX.Element>;
  isOwn: boolean;
  id: string | null | undefined;
};

// Define a type guard to check if a message is a TextConversationMessage
export function isTextConversationMessage(
  message: ConversationMessage,
): message is TextConversationMessage {
  return (
    (message as TextConversationMessage).type === 'text-conversation-message'
  );
}

export function isAssistantEntryPoint(conversation: Conversation) {
  return conversation.context.triggerParams?.triggerPoint?.startsWith(
    'assistant',
  );
}

export function isLiveAnswerV2CurrentlyStreaming(
  message: ConversationMessage | null | undefined,
) {
  if (!message) {
    return false;
  }

  if (
    message.type !== 'inline-data-conversation-message' ||
    message.inlineData?.name !== 'stripe_assistant'
  ) {
    return false;
  }

  const data = message.inlineData?.data;

  if (!data || typeof data !== 'object') {
    return false;
  }

  const dataType = 'type' in data ? (data as {type: string}).type : null;
  const isStripeAssistantLiveAnswersResponse =
    dataType === 'live_answers_v2' || dataType === 'fully_async';

  if (!isStripeAssistantLiveAnswersResponse) {
    return false;
  }

  return message.inlineData.isDone === false;
}

function isCustomPartConversationMessageStreaming(
  message: ConversationMessage,
): boolean {
  if (message.type !== 'custom-part-conversation-message') {
    return false;
  }

  if (message.part.type !== 'ai_response') {
    return false;
  }

  // Prefer message-level dynamic when present. part.dynamic can be absent or
  // stale while a response is still streaming/polling.
  let messageDynamic: string | undefined;
  if (message.data) {
    const {dynamic} = message.data;
    if (typeof dynamic === 'string') {
      messageDynamic = dynamic;
    }
  }

  const dynamicState = messageDynamic ?? message.part.dynamic;
  return dynamicState === 'if_latest' || dynamicState === 'active';
}

export function isFeedbackBlockedWhileStreaming(
  message: ConversationMessage | null | undefined,
) {
  if (!message) {
    return false;
  }

  if (isLiveAnswerV2CurrentlyStreaming(message)) {
    return true;
  }

  if (isCustomPartConversationMessageStreaming(message)) {
    return true;
  }

  return false;
}

export function isFullyAsyncMessage(
  message: ConversationMessage | null | undefined,
): message is InlineDataConversationMessage {
  if (!message) {
    return false;
  }

  const isInlineDataMessage =
    message?.type === 'inline-data-conversation-message';

  if (!isInlineDataMessage) return false;

  const data = message?.inlineData?.data;

  if (!data || typeof data !== 'object') {
    return false;
  }

  const dataType = 'type' in data ? (data as {type: string}).type : null;
  return dataType === 'fully_async';
}

function isMerchantFollowUpCase(item: ConversationLogItem) {
  return (
    item?.name === 'conversation-message-added' &&
    item?.message?.type === 'inline-data-conversation-message' &&
    item?.message?.inlineData?.name === 'merchant_follow_up_case'
  );
}

function isMerchantFollowUpCaseOptions(item: ConversationLogItem) {
  return (
    item?.name === 'conversation-message-added' &&
    item?.message?.type === 'inline-data-conversation-message' &&
    item?.message?.inlineData?.name === 'merchant_follow_up_case_options'
  );
}

function isUniversalHelpMenu(item: ConversationLogItem) {
  return (
    item?.name === 'conversation-message-added' &&
    item?.message?.type === 'inline-data-conversation-message' &&
    item?.message?.inlineData?.name === 'universal_help_channel_menu'
  );
}

export function isEmptyStripeAssistantInlineData(
  item: ConversationLogItem,
): boolean {
  if (
    item?.name !== 'conversation-message-added' ||
    item?.message?.type !== 'inline-data-conversation-message' ||
    item?.message?.inlineData?.name !== 'stripe_assistant' ||
    item?.message?.inlineData?.isDone !== true
  ) {
    return false;
  }

  const {data} = item.message.inlineData;
  if (!Array.isArray(data)) {
    const isLiveAnswersV2 =
      data?.type === 'live_answers_v2' || data?.type === 'fully_async';

    // this is the case where the backend intentionally halted the response, mapping to the isCancelled state
    if (isLiveAnswersV2 && data?.was_halted === true) {
      return false;
    }

    return (
      isLiveAnswersV2 &&
      data?.is_streamed === true &&
      !data.structured_content &&
      (!data?.content || data.content.trim() === '')
    );
  } else {
    return false;
  }
}

export function isEmptyCustomPart(item: ConversationLogItem): boolean {
  if (item?.name !== 'conversation-message-added') {
    return false;
  }
  const {message} = item;
  if (
    message?.type !== 'custom-part-conversation-message' ||
    !('part' in message)
  ) {
    return false;
  }
  const {part} = message as any;
  if (part?.type !== 'ai_response') {
    return false;
  }
  return !part.content?.text || part.content.text.length === 0;
}

function isOwn(
  conversation: Conversation,
  item: ConversationLogItem | null | undefined,
) {
  return (
    item?.name === 'conversation-message-added' &&
    item?.message?.participant.id === conversation.user.id
  );
}

function isResponseExpected(
  item: ConversationLogItem,
  conversation: Conversation,
) {
  if (item.name !== 'conversation-message-added') {
    return false;
  }

  const isFromUser = item.message.participant.id === conversation.user.id;

  const isTextOrSelectedOption =
    item.message.type === 'text-conversation-message' ||
    (item.message.type === 'button-options-conversation-message' &&
      item.message.selectedOptionId != null);

  return isFromUser && isTextOrSelectedOption;
}

function addLoadingIndicators(
  conversation: Conversation,
  item: ConversationLogItem,
  currentState: GroupingState,
  animated: boolean,
  shouldTopAnchor: boolean,
) {
  let loaderRow = null;

  // The typing indicator doesn't optionally use the
  // `GroupedMessagesRowV2` component because we need to control
  // the conditional rendering here, but the typing indicator
  // controls that state internally. That means we can't support
  // this type of loading indicator when scrolling up is enabled;
  // that's ok because this is only for talking to a human agent
  currentState.items.push(
    <TypingIndicatorNext
      key="typing-indicator"
      conversation={conversation}
      animated={animated}
    />,
  );

  // We conditionally show the blinking cursor indicator,
  // inferring that a response is coming from the message. We do
  // this declaratively so the loader is shown instantly after the
  // user message appears
  if (
    isResponseExpected(item, conversation) &&
    conversation.context.channel !== 'chat'
  ) {
    const isFullyAsync =
      item.name === 'conversation-message-added'
        ? isFullyAsyncMessage(item.message)
        : false;

    let loadingText;
    if (isAssistantEntryPoint(conversation)) {
      if (isFullyAsync) {
        loadingText = <LoadingTextAsync />;
      } else {
        loadingText = <LoadingText shouldReset />;
      }
    } else {
      loadingText = <BlinkingCursor />;
    }

    // If top anchoring, we want the cursor to always exist in a
    // new group to ensure that have the full height of the log to
    // scroll up with. Otherwise, we don't group it at all and it
    // exists as a normal item
    if (shouldTopAnchor) {
      loaderRow = (
        <GroupedMessagesRowV2
          key="grouped-loading"
          rows={[loadingText]}
          fillSpace={shouldTopAnchor}
        />
      );
    } else {
      currentState.items.push(loadingText);
    }
  }

  return loaderRow;
}

function shouldItemForceBreak({item}: {item: ConversationLogItem}) {
  return isUniversalHelpMenu(item);
}

function isLoadingAsync(item: ConversationLogItem) {
  return (
    item.name === 'conversation-message-added' &&
    isFullyAsyncMessage(item.message) &&
    !item.message.inlineData.isDone
  );
}

export function shouldBreak(
  conversation: Conversation,
  item: ConversationLogItem,
  prevItem: ConversationLogItem | undefined,
  currentState: GroupingState,
  groupCache: Map<string, string>,
  shouldTopAnchor: boolean,
) {
  if (isLoadingAsync(item) && !shouldTopAnchor) {
    return false;
  }

  const expectedGroupId = groupCache.get(item.id);
  let isBreak = false;

  if (expectedGroupId) {
    isBreak = currentState.id !== expectedGroupId;
  } else if (shouldTopAnchor) {
    const isUnselectedOptions = (
      conversationItem: ConversationLogItem | undefined,
    ) => {
      if (!conversationItem) {
        return false;
      }
      return (
        conversationItem?.name === 'conversation-message-added' &&
        conversationItem?.message?.type ===
          'button-options-conversation-message' &&
        conversationItem?.message?.selectedOptionId == null
      );
    };
    const currentItemWasUnselectedOptions = isUnselectedOptions(item);
    const prevItemWasUnselectedOptions = isUnselectedOptions(prevItem);

    isBreak =
      shouldItemForceBreak({item}) ||
      prevItemWasUnselectedOptions ||
      (isOwn(conversation, item) !== isOwn(conversation, prevItem) &&
        !currentItemWasUnselectedOptions);
  }

  return isBreak;
}

export function useGroupedMessagesRowV2Gap() {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  let gap = 'xsmall';

  if (useNewDesign) {
    gap = 'large';
  } else if (useDotComDesign) {
    gap = 'small';
  }
  return gap;
}

function shouldGroupShowFeedback({
  group,
  conversation,
  responseId,
  disableFeedback,
}: {
  group: JSX.Element[];
  conversation: Conversation;
  responseId: string | undefined;
  disableFeedback?: boolean;
}) {
  if (disableFeedback) {
    return false;
  }
  if (isFeedbackBlockedWhileStreaming(conversation.mostRecentMessage())) {
    return false;
  }

  const hideFeedbackForItem = group.some(
    (item) =>
      isUniversalHelpMenu(item.props?.item) ||
      isMerchantFollowUpCase(item.props?.item) ||
      isMerchantFollowUpCaseOptions(item.props?.item),
  );

  const hasFeedbackAction = group.some(
    (i) => !!i.props?.item?.message?.feedback?.action,
  );

  return (
    hasFeedbackAction &&
    conversation.isActive &&
    responseId &&
    // Always break grouping for universal help channel menu. We break
    // universal help menu into a separate group of one item since it
    // technically comes from the "bot". here we opt out of hover ability
    // it should just be one message as an array[]
    !hideFeedbackForItem
  );
}

function groupByResponseId(
  currentState: GroupingState,
  conversation: Conversation,
  gap: string,
  theme: Theme,
  disableFeedback?: boolean,
) {
  const processedItems: JSX.Element[] = [];
  let accumulator: JSX.Element[] = [];
  let currentResponseId: string | null = null;

  const flushBotGroup = () => {
    if (accumulator.length === 0) {
      return;
    }

    const {responseId, feedback} = accumulator.reduce<{
      responseId?: string;
      feedback?: 'thumbs_up' | 'thumbs_down' | 'none';
    }>(
      (acc, val) => {
        const {item} = val.props;
        const isMessage = item && 'message' in item;
        if (isMessage && item.message.responseId) {
          acc.responseId = item.message.responseId;
        }
        if (isMessage && item.message.feedback) {
          acc.feedback = item.message.feedback.value;
        }
        return acc;
      },
      {responseId: undefined, feedback: undefined},
    );

    const showFeedback = shouldGroupShowFeedback({
      group: accumulator,
      conversation,
      responseId,
      disableFeedback,
    });

    processedItems.push(
      <view.div
        className="response-id-hover-group"
        key={`hover-${accumulator[0].key}`}
        css={{
          position: 'relative',
          padding: '8px',
          paddingBottom: '12px',
          margin: `0 -8px`,
          borderRadius: tokens.border.radius.medium,
          gap,
          display: 'flex',
          flexDirection: 'column',
          ...(showFeedback && {
            ':hover': {
              backgroundColor: theme.surfaceHoverColor,
            },
          }),
        }}
      >
        {accumulator}
        {showFeedback ? (
          <FeedbackContainer
            conversation={conversation}
            responseId={responseId ?? ''}
            feedback={feedback ?? 'none'}
          />
        ) : null}
      </view.div>,
    );

    accumulator = [];
    currentResponseId = null;
  };

  currentState.items.forEach((stateItem) => {
    const logItem = stateItem.props?.item;

    const isConversationMessage =
      logItem?.name === 'conversation-message-added';
    const role = isConversationMessage
      ? logItem.message?.participant.role
      : null;
    const responseId = isConversationMessage
      ? logItem.message?.responseId
      : null;

    if (role === 'bot' && responseId) {
      // Start a new group when the responseId changes
      if (currentResponseId !== responseId) {
        flushBotGroup();
        currentResponseId = responseId;
      }
      accumulator.push(stateItem);
      return;
    }

    flushBotGroup();
    processedItems.push(stateItem);
  });

  flushBotGroup();
  return processedItems;
}

export function useItemsGroupedV2(
  conversation: Conversation,
  items: ConversationLogItem[],
  {
    useNewDesign,
    useDotComDesign,
    animated,
    disableInteraction,
    messageRenderer,
    loading,
    loadingMessage,
    disableFeedback,
    shouldAddLoadingIndicators = true,
  }: GroupingOptions,
) {
  const gap = useGroupedMessagesRowV2Gap();
  const {hasTopAnchorFlag, shouldTopAnchor} =
    useMessagesTopAnchored(conversation);
  const xsmallBorder = useTokenValue(tokens.border.radius.xsmall);
  const groupCache = useMemo(() => new Map<string, string>(), []);
  const theme = useTheme();
  const initialItemIdsRef = useRef<Set<string> | null>(null);
  const initialItemIdsConversationIdRef = useRef<string | null>(null);
  if (
    !initialItemIdsRef.current ||
    initialItemIdsConversationIdRef.current !== conversation.id
  ) {
    initialItemIdsRef.current = new Set(items.map((i) => i.id));
    initialItemIdsConversationIdRef.current = conversation.id;
  }

  if (!hasTopAnchorFlag) {
    return null;
  }

  const rows = [];

  let translationRowAdded = false;
  let conversationParticipantJoined = false;

  let currentState: GroupingState = {
    items: [],
    isOwn: items.length > 0 ? isOwn(conversation, items[0]) : false,
    id: items.length > 0 ? items[0].id : null,
  };

  const groupAndResetState = (item?: ConversationLogItem, isLast?: boolean) => {
    if (currentState.items.length > 0) {
      const rowWithParent = groupByResponseId(
        currentState,
        conversation,
        gap,
        theme,
        disableFeedback,
      );

      rows.push(
        <GroupedMessagesRowV2
          key={`grouped-${currentState.id}`}
          groupId={currentState.id}
          groupOwner={currentState.isOwn ? 'user' : 'other'}
          rows={
            isLast && loading
              ? [
                  ...rowWithParent,
                  <LoadingText
                    customText={loadingMessage}
                    css={{paddingTop: 'small'}}
                  />,
                ]
              : rowWithParent
          }
          fillSpace={isLast}
        />,
      );
    }

    currentState = {
      items: [],
      isOwn: item ? isOwn(conversation, item) : false,
      id: item?.id,
    };
  };

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const prevItem = items[i - 1] || null;
    const nextItem = items[i + 1] || null;

    if (
      shouldBreak(
        conversation,
        item,
        prevItem,
        currentState,
        groupCache,
        !!shouldTopAnchor,
      )
    ) {
      groupAndResetState(item);
    }

    if (!groupCache.has(item.id)) {
      groupCache.set(item.id, currentState.id!);
    }

    switch (item.name) {
      case 'conversation-participant-joined': {
        conversationParticipantJoined = true;
        currentState.items.push(
          <ParticipantJoinedRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-participant-left': {
        currentState.items.push(
          <ParticipantLeftRow
            key={item.id}
            conversation={conversation}
            item={item}
          />,
        );
        break;
      }
      case 'conversation-message-added': {
        const isTranslationCase =
          isTextConversationMessage(item.message) &&
          !!item.message.originalText;

        if (
          isTranslationCase &&
          !translationRowAdded &&
          // Only show this when an agent has joined. Without this
          // condition UH can trigger the row render.
          conversationParticipantJoined
        ) {
          currentState.items.push(<TranslationRow />);
          translationRowAdded = true;
        }

        if (didUserSelectThumbsDownAdvanceButtonOptionsMessage(item)) {
          break;
        }

        if (isEmptyStripeAssistantInlineData(item)) {
          break;
        }

        if (isEmptyCustomPart(item)) {
          const messageDynamic =
            item.message.data &&
            typeof item.message.data === 'object' &&
            'dynamic' in item.message.data
              ? (item.message.data as {dynamic?: string}).dynamic
              : undefined;
          if (messageDynamic !== 'if_latest' && messageDynamic !== 'active') {
            break;
          }
        }

        const avatar = avatarForMessage(item);
        const spacer = avatar ? <AvatarSpacer /> : null;
        const MessageRowElement = (
          <MessageAddedRow
            conversation={conversation}
            key={`grouped-${item.id}`}
            item={item}
            animated={animated}
            isLive={!initialItemIdsRef.current!.has(item.id)}
            avatar={
              prevItem?.name === 'conversation-message-added' &&
              prevItem?.message?.participant.id !== item.message?.participant.id
                ? avatar
                : spacer
            }
            disableInteraction={disableInteraction}
            messageRenderer={messageRenderer}
          />
        );
        if (conversation.isFeedbackV2Enabled) {
          const nextItemLoadingAsync = nextItem
            ? isLoadingAsync(nextItem)
            : false;
          if (nextItemLoadingAsync) {
            const isAgent = item.message?.participant.role === 'agent';
            currentState.items.push(
              <ChatBubbleNextConfig.Customize
                css={{
                  borderBottomRightRadius: !isAgent ? xsmallBorder : undefined,
                  borderBottomLeftRadius: isAgent ? xsmallBorder : undefined,
                }}
              >
                {MessageRowElement}
              </ChatBubbleNextConfig.Customize>,
            );
          } else {
            currentState.items.push(MessageRowElement);
          }
        } else if (MessageRowElement) {
          const isSameParticipant =
            nextItem?.name === 'conversation-message-added' &&
            item.message?.participant.id === nextItem?.message?.participant.id;
          const isAgent = item.message?.participant.role === 'agent';

          currentState.items.push(
            <ChatBubbleNextConfig.Customize
              css={
                // This logic adds CSS if this is the last message for
                // this participant to tighten the border radius. Which
                // corner we tighten depends if the message is on the
                // left or right (if own message or not)
                !isSameParticipant
                  ? {
                      borderBottomRightRadius: !isAgent
                        ? xsmallBorder
                        : undefined,
                      borderBottomLeftRadius: isAgent
                        ? xsmallBorder
                        : undefined,
                    }
                  : {}
              }
            >
              {MessageRowElement}
            </ChatBubbleNextConfig.Customize>,
          );
        }
        break;
      }
      case 'conversation-meta': {
        currentState.items.push(<MetaEventRow key={item.id} item={item} />);
        break;
      }
      case 'conversation-custom-component': {
        currentState.items.push(<CustomComponent {...item} />);
        break;
      }
      case 'conversation-ended': {
        if (!useNewDesign && !conversation.context.surpressChatEndedNotice) {
          currentState.items.push(<ChatEndedRow key={item.id} />);
        }
        break;
      }
      default:
      // unknown log item
    }
  }

  let loaderRow = null;
  if (shouldAddLoadingIndicators) {
    // Once all the items are processed, we run logic to show any
    // necessary loading indicators. This logic may ask to add an
    // additional row to the list
    if (items.length > 0) {
      loaderRow = addLoadingIndicators(
        conversation,
        items[items.length - 1],
        currentState,
        !!animated,
        !!shouldTopAnchor,
      );
    }
  }

  // If we've reached the end, we always want to group the remaining
  // items in the state
  groupAndResetState(undefined, true);

  if (loaderRow && !useDotComDesign) {
    rows.push(loaderRow);
  }

  return rows;
}
